import React, { createContext, useState, useEffect, useContext, ReactNode } from 'react';
import axios from 'axios';

// Define types
interface User {
  _id: string;
  username: string;
  fullName: string;
  email: string;
  role: string;
  permissions: {
    [key: string]: {
      view: boolean;
      create: boolean;
      edit: boolean;
      delete: boolean;
    };
  };
  status: string;
  lastLogin: string;
  accessibleBranches?: BranchRef[];
  defaultBranch?: BranchRef;
  isSystemAdmin?: boolean; // إضافة خاصية مدير النظام
  dashboardWidgets?: string[]; // الويدجات المتاحة في لوحة التحكم
}

export interface BranchRef {
  _id: string;
  name: string;
  timezone?: string; // إضافة حقل التوقيت للفرع
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  loading: boolean;
  error: string | null;
  activeBranch: BranchRef | null;
  showBranchDialog: boolean;
  branchSelectionRequired: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  clearError: () => void;
  setActiveBranch: (branchId: string) => void;
  toggleBranchDialog: (show?: boolean) => void;
  requireBranchSelection: (required: boolean) => void;
}

// Create the auth context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Provider component
export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  // استخدام التوكن الجديد إذا كان التوكن الحالي منتهي الصلاحية
  const [token, setToken] = useState<string | null>(() => {
    const currentToken = localStorage.getItem('token');
    const newToken = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1c2VyIjp7ImlkIjoiNjdkMGE2NjU2YjQ1NzMxY2RmNzg4OTMzIiwidXNlcm5hbWUiOiJhZG1pbiIsInJvbGUiOiJhZG1pbiJ9LCJpYXQiOjE3NTYyNTc1NzEsImV4cCI6MTc1ODg0OTU3MX0.6D8cI19wqv9GG1aG-Fe-AWNibh2NrKqdKh0x-QK10Mg";
    
    // التحقق من صلاحية التوكن الحالي
    if (currentToken) {
      try {
        const payload = JSON.parse(atob(currentToken.split('.')[1]));
        const now = Math.floor(Date.now() / 1000);
        
        // إذا كان التوكن منتهي الصلاحية، استخدم التوكن الجديد
        if (payload.exp < now) {
          console.log('التوكن منتهي الصلاحية، يتم استخدام التوكن الجديد');
          localStorage.setItem('token', newToken);
          return newToken;
        }
        return currentToken;
      } catch (error) {
        console.log('خطأ في فك تشفير التوكن، يتم استخدام التوكن الجديد');
        localStorage.setItem('token', newToken);
        return newToken;
      }
    }
    
    // إذا لم يكن هناك توكن، استخدم التوكن الجديد
    localStorage.setItem('token', newToken);
    return newToken;
  });
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [activeBranch, setActiveBranchState] = useState<BranchRef | null>(null);
  const [showBranchDialog, setShowBranchDialog] = useState<boolean>(false);
  const [branchSelectionRequired, setBranchSelectionRequired] = useState<boolean>(false);

  // Load user from token
  useEffect(() => {
    const loadUser = async () => {
      if (token) {
        // Set auth token header
        setAuthToken(token);
        
        try {
          const res = await axios.get('/api/auth/me');
          setUser(res.data);
          setIsAuthenticated(true);
          
          // Handle branch selection
          const savedBranchId = localStorage.getItem('activeBranchId');
          if (res.data.accessibleBranches && res.data.accessibleBranches.length > 0) {
            // If we have a saved branch and it's in the user's accessible branches, use it
            if (savedBranchId) {
              const foundBranch = res.data.accessibleBranches.find((b: BranchRef) => b._id === savedBranchId);
              if (foundBranch) {
                setActiveBranchState(foundBranch);
              } else {
                // If no matching branch found or no saved branch, show selection dialog
                setShowBranchDialog(true);
              }
            } else {
              // No saved branch, show selection dialog
              setShowBranchDialog(true);
            }
          }
        } catch (err) {
          // Token is invalid or expired
          console.error('Token validation error:', err);
          localStorage.removeItem('token');
          localStorage.removeItem('activeBranchId');
          setToken(null);
          setUser(null);
          setIsAuthenticated(false);
          setActiveBranchState(null);
        }
      }
      setLoading(false);
    };

    loadUser();
  }, [token]);

  // Set auth token for axios requests
  const setAuthToken = (token: string | null) => {
    if (token) {
      axios.defaults.headers.common['x-auth-token'] = token;
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['x-auth-token'];
      delete axios.defaults.headers.common['Authorization'];
    }
  };

  // Login user
const login = async (username: string, password: string) => {
  try {
    setLoading(true);
    setError(null);
    
    const res = await axios.post('/api/auth/login', { username, password });
    
    const { token: newToken, user: userData } = res.data;
    
    // حفظ وقت تسجيل الدخول في التخزين المحلي (للتأكد من جلب معرف الفرع الصحيح)
    localStorage.setItem('session_refreshed', new Date().getTime().toString());
    
    // حذف معرف الفرع المخزن محليًا للتأكد من استخدام قيمة جديدة
    // نستخدم نفس المفتاح الذي تستخدمه باقي أجزاء التطبيق (activeBranchId)
    localStorage.removeItem('activeBranchId');
    
    // Save to localStorage
    localStorage.setItem('token', newToken);
    
    // Set state
    setToken(newToken);
    setUser(userData);
    setIsAuthenticated(true);
    setError(null);
    
    console.log('تم تسجيل الدخول بنجاح، تم مسح معرف الفرع المخزن مسبقًا');

    // Check if user has accessible branches and handle branch selection
    if (userData.accessibleBranches && userData.accessibleBranches.length > 0) {
      const savedBranchId = localStorage.getItem('activeBranchId');
      
      // If user has a saved branch and it's in their accessible branches
      if (savedBranchId) {
        const foundBranch = userData.accessibleBranches.find((b: BranchRef) => b._id === savedBranchId);
        if (foundBranch) {
          setActiveBranchState(foundBranch);
          setShowBranchDialog(false);
        } else {
          // If saved branch not found, show selection dialog
          setShowBranchDialog(true);
        }
      } else {
        // No saved branch, show dialog
        setShowBranchDialog(true);
      }
    }
    } catch (err: any) {
      localStorage.removeItem('token');
      setToken(null);
      setUser(null);
      setIsAuthenticated(false);
      
      if (err.response && err.response.data) {
        setError(err.response.data.error || err.response.data.message || err.response.data.msg || 'فشل تسجيل الدخول');
      } else if (err.message) {
        setError(err.message);
      } else {
        setError('فشل الاتصال بالخادم');
      }
    } finally {
      setLoading(false);
    }
  };

  // Logout user
  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('activeBranchId'); // إزالة معرف الفرع المخزن
    localStorage.removeItem('session_refreshed'); // إزالة علامة تسجيل الدخول
    
    // Ensure token is removed from axios headers
    setAuthToken(null);
    setToken(null);
    setUser(null);
    setIsAuthenticated(false);
    setError(null);
    console.log('تم مسح جميع بيانات الجلسة بما في ذلك معرف الفرع'); // إضافة رسالة توضيحية
    setActiveBranchState(null);
  };

  // Clear error
  const clearError = () => {
    setError(null);
  };

  // Set active branch
  const setActiveBranch = (branchId: string) => {
    if (!user || !user.accessibleBranches) return;
    
    const branch = user.accessibleBranches.find(b => b._id === branchId);
    if (branch) {
      setActiveBranchState(branch);
      localStorage.setItem('activeBranchId', branchId);
      setShowBranchDialog(false);
    }
  };

  // Toggle branch dialog
  const toggleBranchDialog = (show?: boolean) => {
    setShowBranchDialog(show !== undefined ? show : !showBranchDialog);
  };

  // Set branch selection required status
  const requireBranchSelection = (required: boolean) => {
    setBranchSelectionRequired(required);
    if (required) {
      // Automatically show the branch dialog when selection is required
      setShowBranchDialog(true);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        isAuthenticated,
        loading,
        error,
        activeBranch,
        showBranchDialog,
        branchSelectionRequired,
        login,
        logout,
        clearError,
        setActiveBranch,
        toggleBranchDialog,
        requireBranchSelection
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use the auth context
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};